import React, {
  useState,
  createContext,
  useEffect,
  useContext,
  useMemo,
} from "react";
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { APIProviderContext } from "../api";
import { toast } from "react-toastify";
import useInterval from "use-interval";
import _, { get, isEmpty, isEqual } from "lodash";
import { CorrectIcon } from "../../assets/ui-icons";
import Toast from "../toast/toast";
import { remove_prefix } from "../helpers";
import { getVideoDimensionsOf } from "../dom";
import { AccountContext } from "../account/account";
import { GrigoraAccountContext } from "../grigora-account";
import { PUBLISHED_WEBSITE_DOMAIN } from "../app";
import { WhiteLabelContext } from "../whitelabel";

/**
 * Take the difference between the dates and divide by milliseconds per day.
 * Round to nearest whole number to deal with DST.
 */
function datediff(first, second) {
  return Math.round((second - first) / (1000 * 60 * 60 * 24));
}

/**
 * new Date("dateString") is browser-dependent and discouraged, so we'll write
 * a simple parse function for U.S. date format (which does no error checking)
 */
function parseDate(timestamp) {
  return new Date(timestamp * 1000);
}

function addDays(date, days) {
  var result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

const calculateDaysRemain = (createdOn) => {
  if (!createdOn) return 0;

  var diff = Math.max(
    0,
    datediff(new Date(), addDays(parseDate(createdOn), 14)),
  );

  return diff;
};

const GrigoraDashboardContext = createContext();

function GrigoraDashboardProvider(props) {
  const {
    projectData,
    projectDashboardLoad,
    updateProjectData: updateProjectDataAPI,
    enableIntegration: enableIntegrationAPI,
    disableIntegration: disableIntegrationAPI,
    getCollaborators: getCollaboratorsAPI,
    inviteCollaborator: inviteCollaboratorAPI,
    cancelCollaboratorInvite: cancelCollaboratorInviteAPI,
    resendCollaboratorInvite: resendCollaboratorInviteAPI,
    getFreeAnalytics: getFreeAnalyticsAPI,
    getStorageInfo: getStorageInfoAPI,
    getCMSCategories: getCMSCategoriesAPI,
    addCMSCategory: addCMSCategoryAPI,
    updateCMSCategory: updateCMSCategoryAPI,
    deleteCMSCategory: deleteCMSCategoryAPI,
    getNotifications: getNotificationsAPI,
    uploadProjectImage: uploadProjectImageAPI,
    createProjectVideoUploadSignedUrl,
    createProjectVideoConfirmSignedUpload,
    uploadFileS3,
    apiAction,
  } = useContext(APIProviderContext) ?? {};

  const {
    isSubscribed: isAccountSubscribed,
    subscriptionPlan: accountSubscriptionPlan,
  } = useContext(GrigoraAccountContext) ?? {};

  const { userAttributes } = useContext(AccountContext) ?? {};

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [editorDocument, setEditorDocument] = useState(undefined);
  const [editorWindow, setEditorWindow] = useState(undefined);
  const [screen, setScreen] = useState(undefined);
  const { projectID, setting, page } = useParams();
  const [project, setProjectData] = useState(null);

  const [projectCategories, setCategories] = useState(undefined);
  const [activeScreen, setActiveScreen] = useState("");
  const [notifications, setNotifications] = useState({});
  const [notificationsLastKey, setNotificationsLastKey] = useState({});
  const [cmsPosts, setCMSPosts] = useState([]);

  const [blockStates, setBlockStates] = useState({});

  const [directoryTypes, setDirectoryTypes] = useState([]);

  const { __whitelabel_website_publishing_domain__ } =
    useContext(WhiteLabelContext) ?? {};

  const domain = useMemo(() => {
    return project?.domain
      ? project?.domain
      : `${project?.slug}.${__whitelabel_website_publishing_domain__()}`;
  }, [project?.domain, project?.slug]);

  const isProjectSubscribed = useMemo(() => {
    if (userAttributes?.email?.endsWith("@grigora.co")) {
      return true;
    }

    return project?.subscription?.status === "active";
  }, [project?.subscription?.status]);

  const projectSubscriptionPlan = useMemo(() => {
    if (userAttributes?.email?.endsWith("@grigora.co")) {
      return "enterprise";
    }

    return project?.subscription?.plan ?? "free";
  }, [project?.subscription?.plan]);

  var isSubscribed;
  var subscriptionPlan;

  if (isProjectSubscribed) {
    isSubscribed = true;
    subscriptionPlan = projectSubscriptionPlan;
  } else if (isAccountSubscribed && !project?.isExternalSubscription) {
    isSubscribed = true;
    subscriptionPlan = accountSubscriptionPlan;
  } else {
    isSubscribed = false;
    subscriptionPlan = "free";
  }

  const trialDaysRemaining = useMemo(() => {
    return calculateDaysRemain(project?.created_on);
  }, [project?.created_on]);

  const isFreeTrialActive = useMemo(() => {
    return !isSubscribed && trialDaysRemaining > 0;
  }, [isSubscribed, trialDaysRemaining]);

  const projectMembers = useMemo(() => {
    var newMembers = {};

    Object.keys(project?.members ?? {}).forEach((memberID) => {
      if (project?.members[memberID]?.whitelabelAdmin) {
        return;
      }

      newMembers[memberID] = project?.members[memberID];
    });

    return newMembers;
  }, [project?.members]);

  const resetStates = () => {
    setIsLoading(true);
    setProjectData(null);
    setCategories(undefined);
    setDirectoryTypes([]);
    setNotifications({});
    setNotificationsLastKey({});
    setBlockStates({});
  };

  useEffect(() => {
    resetStates();

    projectDashboardLoad?.({ id: projectID })
      .then((data) => {
        if (!isEmpty(data.project)) {
          setProjectData(data.project);
          setCategories(data.categories);
          setNotifications(getFilteredProjectNotifications(data.notifications));
          // getNotificationsAPI("read").then((data) => {
          // console.log(data);
          // setNotifications(getFilteredProjectNotifications(data));
          // });
          setIsLoading(false);
        } else {
          // redirect to 404
          navigate("/404", { replace: true });
        }
      })
      .catch((err) => {
        resetStates();
      });
    return () => {
      resetStates();
    };
  }, [projectID]);

  useEffect(() => {
    if (projectID && userAttributes?.sub) {
      apiAction("POST", "project/directory/list", {
        user_id: userAttributes.sub,
        project_id: projectID,
      })
        .then((data) => {
          setDirectoryTypes(data?.Output || []);
        })
        .catch((err) => {
          console.log(err);
          toast.error(<Toast title={"Failed to load directory types"} />);
        });
    }
  }, [projectID, userAttributes?.sub]);

  const loadReadNotifications = () => {
    getNotificationsAPI("read", projectID, notificationsLastKey).then(
      (data) => {
        const obj = {};

        for (const item of data?.Output ?? []) {
          var notificationID = remove_prefix(item.sk, "read-");
          obj[notificationID] = {
            ...item[notificationID],
            read: true,
          };
        }

        console.log(obj, data?.LastEvaluatedKey);

        setNotificationsLastKey(data?.LastEvaluatedKey);
        console.log(data?.LastEvaluatedKey);
        setNotifications((old) => ({
          ...old,
          ...getFilteredProjectNotifications(obj),
        }));
      },
    );
  };

  const uploadProjectImage = async (image) => {
    return await new Promise((resolve, reject) => {
      uploadProjectImageAPI(projectID, image)
        .then((res) => {
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve(res.data.Output.id);
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const uploadProjectVideo = async (video) => {
    return await new Promise((resolve, reject) => {
      if (!video?.name) {
        toast.error(<Toast title={"Error"} description={"Invalid Video"} />);
        return;
      }
      getVideoDimensionsOf(video)
        .then((dimensions) => {
          createProjectVideoUploadSignedUrl(projectID, video.name)
            .then((data) => {
              var uploadURL = data?.data?.Output?.url;
              var videoID = data?.data?.Output?.videoID;

              uploadFileS3(uploadURL, video, video.type)
                .then(() => {
                  createProjectVideoConfirmSignedUpload(
                    projectID,
                    videoID,
                    dimensions.width,
                    dimensions.height,
                  )
                    .then((data) => {
                      resolve({
                        pk: "project-" + projectID,
                        sk: "video-" + videoID,
                      });
                      toast.success(<Toast title={"Video Uploaded."} />, {
                        icon: <CorrectIcon />,
                      });
                    })
                    .catch((err) => {
                      console.log(err);
                      toast.error(
                        <Toast
                          title={"Error"}
                          description={err?.response?.data?.Output?.message}
                        />,
                      );
                      reject();
                    });
                })
                .catch((err) => {
                  console.log(err);
                  toast.error(
                    <Toast
                      title={"Error"}
                      description={err?.response?.data?.Output?.message}
                    />,
                  );
                  reject();
                });
            })
            .catch((err) => {
              console.log(err);
              toast.error(
                <Toast
                  title={"Error"}
                  description={err?.response?.data?.Output?.message}
                />,
              );
              reject();
            });
        })
        .catch((err) => {
          console.log(err);
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  // Fetch Notifications every 2 minutes
  useInterval(() => {
    getNotificationsAPI("unread").then((data) => {
      setNotifications((old) => ({
        ...getFilteredProjectNotifications(data),
        ...old,
      }));
    });
  }, 2 * 60 * 1000);

  // Fetch Notifications every 2 minutes
  // useInterval(() => {
  //   getNotificationsAPI("unread").then((data) => {
  //     setNotifications(getFilteredProjectNotifications(data));
  //   });
  // }, 2 * 60 * 1000);

  useEffect(() => {
    if (!!matchPath("/site/:projectID/", pathname)) {
      setActiveScreen("dashboard");
    } else if (!!matchPath("/site/:projectID/cms/posts", pathname)) {
      setActiveScreen("cms/posts");
    } else if (!!matchPath("/site/:projectID/cms/addpost", pathname)) {
      setActiveScreen("cms/addpost");
    } else if (!!matchPath("/site/:projectID/newsletter", pathname)) {
      setActiveScreen("newsletter");
    } else if (!!matchPath("/site/:projectID/seo-report", pathname)) {
      setActiveScreen("seo-report");
    } else if (
      !!matchPath("/site/:projectID/google-analytics-report", pathname)
    ) {
      setActiveScreen("google-analytics-report");
    } else if (!!matchPath("/site/:projectID/keyword-research", pathname)) {
      setActiveScreen("keyword-research");
    } else if (!!matchPath("/site/:projectID/cms/category", pathname)) {
      setActiveScreen("cms/category");
    } else if (!!matchPath("/site/:projectID/cms/addcategory", pathname)) {
      setActiveScreen("cms/addcategory");
    } else if (
      !!matchPath("/site/:projectID/cms/editcategory/:categoryID", pathname)
    ) {
      setActiveScreen("cms/editcategory");
    } else if (!!matchPath("/site/:projectID/cms/editpost/:postID", pathname)) {
      setActiveScreen("cms/editpost");
    } else if (!!matchPath("/site/:projectID/editor", pathname)) {
      setActiveScreen("editor");
    } else if (!!matchPath("/site/:projectID/templates", pathname)) {
      setActiveScreen("templates");
    } else if (!!matchPath("/site/:projectID/teams/:page", pathname)) {
      setActiveScreen("teams/" + page);
    } else if (!!matchPath("/site/:projectID/files", pathname)) {
      setActiveScreen("files");
    } else if (!!matchPath("/site/:projectID/settings/:setting", pathname)) {
      setActiveScreen("settings/" + setting);
    } else if (!!matchPath("/site/:projectID/support", pathname)) {
      setActiveScreen("support");
    } else if (!!matchPath("/site/:projectID/cms/preview/:postID", pathname)) {
      setActiveScreen("cms-preview");
    } else if (!!matchPath("/site/:projectID/directory/listings", pathname)) {
      setActiveScreen("directory/listings");
    } else if (
      !!matchPath("/site/:projectID/directory/listings/:typeID", pathname)
    ) {
      setActiveScreen("directory/listings-type");
    } else if (
      !!matchPath("/site/:projectID/directory/listings/:typeID/add", pathname)
    ) {
      setActiveScreen("directory/listings-add");
    } else if (
      !!matchPath(
        "/site/:projectID/directory/listings/:typeID/edit/:listingID",
        pathname,
      )
    ) {
      setActiveScreen("directory/listings-edit");
    } else if (
      !!matchPath("/site/:projectID/directory/configurations", pathname)
    ) {
      setActiveScreen("directory/configurations");
    } else if (
      !!matchPath("/site/:projectID/directory/configurations/add", pathname)
    ) {
      setActiveScreen("directory/configurations-add");
    } else if (
      !!matchPath(
        "/site/:projectID/directory/configurations/edit/:typeID",
        pathname,
      )
    ) {
      setActiveScreen("directory/configurations-edit");
    } else if (!!matchPath("/site/:projectID/forms", pathname)) {
      setActiveScreen("forms");
    } else {
      setActiveScreen("");
    }
  }, [pathname]);

  const blockState = (id) => {
    return blockStates[id];
  };

  const setBlockState = (id, key, value) => {
    setBlockStates({
      ...blockStates,
      [id]: {
        ...(blockStates[id] ? blockStates[id] : {}),
        [key]: value,
      },
    });
  };

  const getFilteredProjectNotifications = (data) => {
    var filteredNotifications;
    if (data) {
      filteredNotifications = Object.keys(data).reduce(
        (obj, notificationID) => {
          if (
            data[notificationID] &&
            data[notificationID]?.projectID === projectID
          ) {
            obj[[notificationID]] = data[notificationID];
          }
          return obj;
        },
        {},
      );
    } else {
      filteredNotifications = {};
    }
    return filteredNotifications;
  };

  async function inviteCollaborator(collaborator_email, role) {
    return await new Promise((resolve, reject) => {
      inviteCollaboratorAPI(projectID, collaborator_email, role)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function cancelCollaboratorInvite(collaborator_email) {
    return await new Promise((resolve, reject) => {
      cancelCollaboratorInviteAPI(projectID, collaborator_email)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function resendCollaboratorInvite(collaborator_email) {
    return await new Promise((resolve, reject) => {
      resendCollaboratorInviteAPI(projectID, collaborator_email)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function getCMSCategories() {
    return await new Promise((resolve, reject) => {
      getCMSCategoriesAPI(projectID)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function addCMSCategory(
    name,
    slug,
    description,
    thumbnail,
    thumbnail_meta,
  ) {
    return await new Promise((resolve, reject) => {
      addCMSCategoryAPI(
        projectID,
        name,
        slug,
        description,
        thumbnail,
        thumbnail_meta,
      )
        .then((res) => {
          setCategories([
            ...projectCategories,
            {
              ...res.category,
            },
          ]);
          resolve(res.category);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }
  async function updateCMSCategory(
    id,
    name,
    slug,
    description,
    thumbnail,
    thumbnail_meta,
  ) {
    return await new Promise((resolve, reject) => {
      updateCMSCategoryAPI(
        projectID,
        id,
        name,
        slug,
        description,
        thumbnail,
        thumbnail_meta,
      )
        .then((res) => {
          var temp = [...projectCategories];
          var objIndex = temp.findIndex(
            (obj) => obj.sk == `cms-category-${id}`,
          );
          temp[objIndex] = {
            ...temp[objIndex],
            ...(name ? { name: name } : {}),
            ...(slug ? { slug: slug } : {}),
            ...(description ? { description: description } : {}),
            ...(thumbnail ? { thumbnail: thumbnail } : {}),
            ...(thumbnail_meta ? { thumbnail_meta: thumbnail_meta } : {}),
          };
          setCategories(temp);
          resolve(res.category);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function deleteCMSCategory(id) {
    return await new Promise((resolve, reject) => {
      deleteCMSCategoryAPI(projectID, id)
        .then((res) => {
          var temp = [...projectCategories];
          temp = temp.filter((item) => item.sk !== `cms-category-${id}`);
          setCategories(temp);
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function getCollaborators() {
    return await new Promise((resolve, reject) => {
      getCollaboratorsAPI(projectID)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function enableIntegration(integration) {
    return await new Promise((resolve, reject) => {
      const currentObject = {
        ...project,
        [integration]: true,
      };
      enableIntegrationAPI(projectID, integration)
        .then((res) => {
          setProjectData(currentObject);
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function disableIntegration(integration) {
    return await new Promise((resolve, reject) => {
      const currentObject = {
        ...project,
        [integration]: false,
      };
      disableIntegrationAPI(projectID, integration)
        .then((res) => {
          setProjectData(currentObject);
          resolve(res.data?.Output);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  const updateProjectSiteDetails = async (
    name,
    slug,
    removeMadeWithGrigora,
  ) => {
    return await new Promise((resolve, reject) => {
      updateProjectDataAPI({
        project_id: projectID,
        name: name && name !== project.name ? name : undefined,
        slug: slug && slug !== project.slug ? slug : undefined,
        removeMadeWithGrigora:
          removeMadeWithGrigora !== !!project.removeMadeWithGrigora
            ? removeMadeWithGrigora
            : undefined,
      })
        .then((res) => {
          var newSlug = res?.data?.Output?.slug;

          setProjectData({
            ...project,
            name: name && name !== project.name ? name : project.name,
            slug: newSlug
              ? newSlug
              : slug && slug !== project.slug
              ? slug
              : project.slug,
            removeMadeWithGrigora:
              removeMadeWithGrigora !== !!project.removeMadeWithGrigora
                ? removeMadeWithGrigora
                : project.removeMadeWithGrigora,
          });
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve({
            newSlug: newSlug,
          });
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );

          reject();
        });
    });
  };

  const updateProjectCustomCode = async (customcode) => {
    return await new Promise((resolve, reject) => {
      updateProjectDataAPI({
        project_id: projectID,
        customcode: !isEqual(customcode, project.customcode)
          ? customcode
          : undefined,
      })
        .then((res) => {
          setProjectData({
            ...project,
            customcode: !isEqual(customcode, project.customcode)
              ? customcode
              : undefined,
          });
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const updateProjectCustomFontsState = async (customFonts) => {
    setProjectData({ ...project, customFonts: customFonts });
  };

  const setProjectFonts = async (projectFonts) => {
    var temp = { ...project, fonts: projectFonts };
    setProjectData(temp);
  };

  const updateprojectFonts = async (projectFonts) => {
    return await new Promise((resolve, reject) => {
      if (
        projectFonts &&
        Object.keys(projectFonts).length === 0 &&
        projectFonts.constructor === Object
      ) {
        projectFonts = "empty";
      }
      updateProjectDataAPI({ project_id: projectID, fonts: projectFonts })
        .then((res) => {
          setProjectData({
            ...project,
            fonts: projectFonts ? projectFonts : project.fonts,
          });
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const setProjectColors = async (projectColors) => {
    var tempColors = { ...project, colors: projectColors };
    setProjectData(tempColors);
  };

  const updateProjectColors = async (projectColors, variants) => {
    return await new Promise((resolve, reject) => {
      if (projectColors === undefined || projectColors.length === 0) {
        projectColors = "empty";
      }
      if (variants === undefined || variants?.length === 0) {
        variants = "empty";
      }
      updateProjectDataAPI({
        project_id: projectID,
        colors: projectColors,
        colorVariants: variants,
      })
        .then((res) => {
          setProjectData({
            ...project,
            colors: projectColors ? projectColors : project.colors,
          });
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  async function getFreeAnalytics() {
    return await new Promise((resolve, reject) => {
      getFreeAnalyticsAPI(projectID)
        .then((res) => {
          resolve(res.data?.Output);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async function getStorageInfo() {
    return await new Promise((resolve, reject) => {
      getStorageInfoAPI(projectID)
        .then((res) => {
          resolve(res.data?.Output);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  function getImgSrcUrl(src, srcMode, project_id = projectID) {
    if (srcMode === "cdn") {
      if (src?.og) {
        return `https://cdn.grigora.co/projects/${project_id}/${src?.og}`;
      }
      return `https://cdn.grigora.co/projects/${project_id}/${src?.name}.${src?.extension}`;
    } else if (srcMode === "link") {
      return src;
    } /* only in editor */ else if (srcMode === "preview") {
      return src;
    }
  }

  function removeInternalNotifications(notificationIDs) {
    var tempUnread = { ...notifications };
    notificationIDs.forEach((notificationID) => {
      tempUnread[notificationID] = {
        ...tempUnread[notificationID],
        read: true,
      };
    });
    setNotifications((old) => ({ ...old, ...tempUnread }));
  }

  return (
    <GrigoraDashboardContext.Provider
      value={{
        inviteCollaborator,
        isLoading,
        projectID,
        project,
        setProjectData,
        enableIntegration,
        disableIntegration,
        editorDocument,
        setEditorDocument,
        editorWindow,
        setEditorWindow,
        updateprojectFonts,
        setProjectFonts,
        screen,
        setScreen,
        setProjectColors,
        updateProjectColors,
        getFreeAnalytics,
        getStorageInfo,
        domain,
        projectCategories,
        addCMSCategory,
        updateCMSCategory,
        activeScreen,
        getCollaborators,
        cancelCollaboratorInvite,
        resendCollaboratorInvite,
        updateProjectSiteDetails,
        updateProjectCustomCode,
        getImgSrcUrl,
        notifications,
        loadReadNotifications,
        setNotifications,
        removeInternalNotifications,
        deleteCMSCategory,
        setCategories,
        blockState,
        setBlockState,
        isSubscribed,
        notificationsLastKey,
        subscriptionPlan,
        uploadProjectImage,
        uploadProjectVideo,
        cmsPosts,
        setCMSPosts,
        isFreeTrialActive,
        trialDaysRemaining,
        directoryTypes,
        setDirectoryTypes,
        projectMembers,
        updateProjectCustomFontsState,
      }}
    >
      {props.children}
    </GrigoraDashboardContext.Provider>
  );
}

export { GrigoraDashboardProvider, GrigoraDashboardContext };
